import { FirstDataRenderedEvent, GridApi, GridOptions, IRowNode, ColDef } from '@ag-grid-community/core';
import { customEvent } from '@genesislcap/foundation-events';
import {
  attr,
  css,
  customElement,
  GenesisElement,
  html,
  observable,
  when,
} from '@genesislcap/web-core';
import { BadgeRenderer, ConditionsRenderer } from '../../cell-renderers';
import {
  conditionsSummaryColDef,
  couponColDef,
  maturityAmountColDef,
  maturityDateColDef,
  putDateColDef,
  priceColDef,
  getEconomicsColumnDefs,
  statusColDef,
  yieldColDef,
  createNotesColDef,
} from '../../deal-series/deal-series.col-defs';
import { MaturityOrder, statusCanCancel } from '../../deal-series/deal-series.types';
import {
  formatOrderQuantityForCell,
  formatToThreeDecimals,
  orderAmountFormatter,
  conditionsTooltipFormatter,
  seriesSizeFormatter,
  shouldShowPutDateColumn,
  isZeroCouponMaturity,
} from '../../deal-series/deal-series.util';

import { getSubmitModalRowKey, getSubmitOrdersSnapshotSignature } from '../../deal-series/submit-preview-order.util';
import {
  getQuantityColumnValidationColDef,
  quantityValidationGridSlottedStyles,
} from '../../deal-series/deal-series.quantity-validation';
import { sharedMuniGridStyles } from '../../deals.styles';
import { getFixedWidthConfig } from '../../deals.util';
import { BondSeries } from '../deal-detail.types';
import {
  isOrderRelevantForSubmitModalList,
  isOrderSelectableInSubmitModal,
  isOrderValidForCancellation,
} from './deal-submit-preview-grid.utils';
import { DealStatusEnum, TaxStatusEnum } from '../../deals.types';
import {
  createPriceChangeCellRenderer,
  pinnedAwareFormatter,
  refreshPriceChangeColumns,
} from '../reprice-window-grid.utils';
import { getStatusIconConfig } from '../deal-reconfirm.utils';
import { isTermBondMaturity } from '../../deal-series/sinking-fund-schedule.util';

const PREVIEW_ECONOMICS_COLUMN_WIDTH = 110;

const withPreviewEconomicsColumnWidth = (col: ColDef): ColDef => {
  if (
    col.field === 'SPREAD' ||
    col.field === 'TAX_TREASURY_SPREAD' ||
    col.colId === 'UST_BENCHMARK'
  ) {
    return { ...col, width: PREVIEW_ECONOMICS_COLUMN_WIDTH };
  }
  return col;
};

const previewGridStyles = css`
  .ag-center-cols-clipper {
    // Needed as default min height is 150px
    min-height: unset !important;
  }

  .ag-cell.term-bond-par-amount {
    color: #4da3ff !important;
  }

  ${quantityValidationGridSlottedStyles}
`;

@customElement({
  name: 'deal-submit-preview-grid',
  template: html<DealSubmitPreviewGrid>`
    <div class="deal-submit-preview-grid">
      <rapid-divider></rapid-divider>
      <div class="deal-submit-preview-grid-header">${(x) => seriesSizeFormatter(x.series?.SERIES_CODE, x.series?.SERIES_SIZE)}</div>
      <rapid-divider></rapid-divider>

      <deal-series-information 
        :series="${(x) => x.series}"
        :bondSeriesList="${(x) => x.bondSeriesList || [x.series]}"
      ></deal-series-information>

      ${when(
        (x) => x.inRepriceWindow && x.displayedRowCount === 0,
        html<DealSubmitPreviewGrid>`
          <div class="deal-submit-preview-grid-empty">No orders in this series</div>
        `,
      )}

      <div class="deal-submit-preview-grid-grid ${(x) => (x.inRepriceWindow && x.displayedRowCount === 0 ? 'deal-submit-preview-grid-grid--hidden' : '')}">
        <rapid-grid-pro
          @gridReady="${(x, ctx) => x.handleGridReady(customEvent(ctx))}"
          :gridOptions="${(x) => x.gridOptions}"
        >
          <slotted-styles :styles="${() => previewGridStyles}"></slotted-styles>
        </rapid-grid-pro>
      </div>
    </div>
  `,
  styles: css`
    ${sharedMuniGridStyles}

    /* Remove selected-row highlight only for this modal */
    .deal-submit-preview-grid rapid-grid-pro::part(grid) {
      --ag-selected-row-background-color: transparent;
    }

    .deal-submit-preview-grid-empty {
      padding: 10px 0;
      font-size: 14px;
    }

    .deal-submit-preview-grid-header {
      font-weight: bold;
      margin: 10px 0;
    }

    .deal-submit-preview-grid-grid--hidden {
      display: none;
    }
  `,
})
export class DealSubmitPreviewGrid extends GenesisElement {
  @attr({ attribute: 'call-feature-description' }) callFeatureDescription: string;

  @attr({ mode: 'boolean' }) cancellation: boolean;

  /** Deal is in Verbal Award/CXL Window; submit modal shows reconfirm-eligible + edited orders. */
  @attr({ attribute: 'in-reprice-window', mode: 'boolean' }) inRepriceWindow: boolean;
  inRepriceWindowChanged(): void {
    if (this.rowData != null) this.applyOrdersFilter();
  }

  /** When true (e.g. in reprice window), cancel modal only allows cancelling affected (reconfirm-eligible) orders. */
  @attr({ attribute: 'cancel-only-affected-orders', mode: 'boolean' }) cancelOnlyAffectedOrders: boolean;
  cancelOnlyAffectedOrdersChanged(): void {
    this.updateGridContextAndFilter();
  }

  /** When true (reprice window), at least one order is SUBMITTED_AND_DRAFT with price-change flag. */
  @attr({ attribute: 'has-edited-order-in-deal', mode: 'boolean' }) hasAnyEditedOrderInDeal: boolean;
  hasAnyEditedOrderInDealChanged(): void {
    if (this.rowData != null) this.applyOrdersFilter();
  }

  @attr({ attribute: 'deal-status' }) dealStatus: string | null;
  dealStatusChanged(): void {
    if (this.series?.TAX_STATUS) {
      this.setGridOptions(this.series.TAX_STATUS as TaxStatusEnum);
      if (this.gridApi) {
        this.gridApi.setColumnDefs(this.gridOptions.columnDefs);
        this.gridApi.refreshCells({ force: true });
      }
    }
  }

  @observable series: BondSeries;

  @observable bondSeriesList: BondSeries[];

  @observable rowData: MaturityOrder[] = [];
  rowDataChanged(): void {
    this.applyOrdersFilter();
    this.syncGridRowData();
  }

  @observable ordersForState: MaturityOrder[] = [];
  ordersForStateChanged(): void {
    if (this.gridApi) {
      this.gridApi.onFilterChanged();
      this.scheduleAutoSelectIfNeeded();
    }
  }

  @observable displayedRowCount = 0;

  seriesChanged() {
    this.setGridOptions(this.series.TAX_STATUS as TaxStatusEnum);
    if (this.gridApi) {
      this.gridApi.setColumnDefs(this.gridOptions.columnDefs);
      this.updateGridContextAndFilter();
      this.syncGridRowData();
    }
  }

  private updateGridContextAndFilter(): void {
    if (!this.gridApi || !this.gridOptions) return;
    refreshPriceChangeColumns(this.gridApi);
  }

  private applyOrdersFilter(): void {
    const orders = this.rowData;
    if (!orders?.length) {
      this.ordersForState = [];
      return;
    }
    this.ordersForState = this.cancellation
      ? this.cancelOnlyAffectedOrders
        ? orders.filter((o) => isOrderValidForCancellation(o, true))
        : orders.filter((o) => o.ORDER_STATUS != null && statusCanCancel.has(o.ORDER_STATUS))
      : orders.filter((o) => isOrderRelevantForSubmitModalList(o, this.inRepriceWindow));
  }

  private refreshExternalFilter(): void {
    if (!this.gridApi) return;
    this.gridApi.onFilterChanged();
    this.updateDisplayedRowCount();
  }

  private updateDisplayedRowCount(): void {
    if (!this.gridApi) {
      this.displayedRowCount = 0;
      return;
    }
    this.displayedRowCount = this.gridApi.getDisplayedRowCount();
  }

  /** Called when the modal reopens so filter and selection state refresh. */
  prepareForOpen(): void {
    this.isInitialLoad = true;
    this.pendingAutoSelect = true;
    this.lastSyncedRowSignature = null;
    if (this.rowData != null) {
      this.applyOrdersFilter();
    }
    if (this.gridApi) {
      this.syncGridRowData();
      this.refreshExternalFilter();
      this.syncSelectionForOpen();
    }
  }

  /** Re-sync parent selection after modal open; auto-select when grid data is already loaded. */
  private syncSelectionForOpen(): void {
    if (!this.gridApi) return;
    if (this.cancellation) {
      this.gridApi.deselectAll();
      this.emitSelected();
      this.isInitialLoad = false;
      this.pendingAutoSelect = false;
      return;
    }
    this.scheduleAutoSelectIfNeeded();
  }

  @observable gridOptions: GridOptions;

  private setGridOptions(taxStatus: TaxStatusEnum): void {
    const quantityValidationColDef = getQuantityColumnValidationColDef({
      getDealStatus: () => this.dealStatus,
      pinnedRowCellStyle: { textAlign: 'right' },
    });
    const getIsInRepriceWindow = () => !!this.inRepriceWindow;
    const columnDefs: ColDef[] = [
      ...(this.inRepriceWindow && !this.cancellation
        ? []
        : [
            {
              field: 'selected',
              headerName: '',
              checkboxSelection: true,
              headerCheckboxSelection: true,
              cellStyle: {
                height: '40px',
                display: 'flex',
                alignItems: 'center',
              },
              ...getFixedWidthConfig(30),
            } as ColDef,
          ]),
      {
        ...statusColDef,
        field: 'ORDER_STATUS',
        headerName: 'Order Status',
        cellRendererParams: (params) =>
          getStatusIconConfig(
            params,
            this.inRepriceWindow ? DealStatusEnum.VERBAL_AWARD_CXL_WINDOW : undefined,
          ),
      },
      maturityDateColDef,
      ...(shouldShowPutDateColumn(this.series?.SECURITY_TYPE) ? [putDateColDef] : []),
      {
        ...maturityAmountColDef,
        width: 120,
        cellClassRules: {
          'term-bond-par-amount': (params) =>
            isTermBondMaturity(params.data?.SINKING_FUND_SCHEDULE) ||
            isZeroCouponMaturity(params.data?.SECURITY_TYPE),
        },
        cellRenderer: createPriceChangeCellRenderer(getIsInRepriceWindow, 'MATURITY_AMOUNT_CHANGED', pinnedAwareFormatter(orderAmountFormatter)),
      },
      {
        ...couponColDef,
        cellRenderer: createPriceChangeCellRenderer(getIsInRepriceWindow, 'COUPON_CHANGED', pinnedAwareFormatter(formatToThreeDecimals)),
      },
      {
        ...yieldColDef,
        width: 60,
        cellRenderer: createPriceChangeCellRenderer(getIsInRepriceWindow, 'YIELD_CHANGED', pinnedAwareFormatter(formatToThreeDecimals)),
      },
    ];

    const economicsColumnDefs = getEconomicsColumnDefs(taxStatus);
    columnDefs.push(
      ...economicsColumnDefs.beforePrice.map(withPreviewEconomicsColumnWidth),
    );

    columnDefs.push(
      {
        ...priceColDef,
        cellRenderer: createPriceChangeCellRenderer(getIsInRepriceWindow, 'PRICE_CHANGED', pinnedAwareFormatter(formatToThreeDecimals)),
      },
      ...economicsColumnDefs.afterPrice.map(withPreviewEconomicsColumnWidth),
      createNotesColDef(),
      {
        field: 'QUANTITY',
        headerName: 'Order Amount ($M)',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: (params) => formatOrderQuantityForCell(params.data),
        ...getFixedWidthConfig(140),
        width: 140,
        ...quantityValidationColDef,
      },
      {
        ...conditionsSummaryColDef,
        tooltipValueGetter: (params) => conditionsTooltipFormatter(params.data),
      }
    );

    const isExternalFilterPresent = () =>
      (this.inRepriceWindow && !this.cancellation) ||
      (this.cancellation && this.cancelOnlyAffectedOrders);
    const doesExternalFilterPass = (node: IRowNode<MaturityOrder>) => {
      const id = node.data?.MATURITY_ID;
      if (id == null) return false;
      const idStr = String(id);
      return this.ordersForState?.some((o) => String(o.MATURITY_ID) === idStr) ?? false;
    };

    this.gridOptions = {
      columnDefs,
      isExternalFilterPresent,
      doesExternalFilterPass,
      getRowId: (params) => {
        const { ORDER_ID, MATURITY_ID, LINE_NO, SPLIT_LINE_NO } = params.data;
        const lineNo = LINE_NO ?? SPLIT_LINE_NO ?? 0;
        const baseId = ORDER_ID || MATURITY_ID;
        return lineNo > 0 ? `${baseId}-${lineNo}` : String(baseId);
      },
      defaultColDef: {
        filter: true,
        resizable: false,
        editable: false,
        sortable: false,
        suppressMenu: true,
      },
      domLayout: 'autoHeight',
      suppressContextMenu: true,
      rowHeight: 40,
      tooltipShowDelay: 0,
      components: {
        badgeRenderer: BadgeRenderer,
        conditionsRenderer: ConditionsRenderer,
      },
      onSelectionChanged: () => {
        if (this.batchSelecting) return;
        this.emitSelected();
      },
      isRowSelectable: (params) =>
        this.inRepriceWindow && !this.cancellation
          ? false
          : this.cancellation
            ? isOrderValidForCancellation(params.data, this.cancelOnlyAffectedOrders)
            : isOrderSelectableInSubmitModal(params.data),
      rowSelection: 'multiple' as const,
      suppressRowClickSelection: true,
      suppressMovableColumns: true,
      onFirstDataRendered: (event: FirstDataRenderedEvent<any>) => {
        this.updateDisplayedRowCount();
        this.scheduleAutoSelectIfNeeded();
        event.api.sizeColumnsToFit();
      },
      onRowDataUpdated: () => {
        this.updateDisplayedRowCount();
        this.scheduleAutoSelectIfNeeded();
      },
      onFilterChanged: () => this.updateDisplayedRowCount(),
    };
  }

  private gridApi: GridApi;

  private gridResizeTimeoutRef;

  private isInitialLoad = true;

  /** Set when the submit modal opens; cleared after auto-select runs or no selectable rows remain. */
  private pendingAutoSelect = false;

  private autoSelectFrameRef: number | null = null;

  /** Suppresses onSelectionChanged while programmatically batch-selecting (deselectAll clears parent state). */
  private batchSelecting = false;

  private lastSyncedRowSignature: string | null = null;

  /** Push req-rep row data into AG Grid; use force when poll/refresh must repaint (e.g. conditions summary). */
  updateRowData(orders: MaturityOrder[], force = false): void {
    if (force) {
      this.lastSyncedRowSignature = null;
    }
    this.rowData = orders;
  }

  private syncGridRowData(force = false): void {
    if (!this.gridApi) return;

    const nextRowData = this.rowData ?? [];
    const signature = getSubmitOrdersSnapshotSignature(nextRowData);
    if (!force && signature === this.lastSyncedRowSignature) return;

    const selectedKeys = new Set<string>();
    this.gridApi.getSelectedNodes().forEach((node) => {
      const key = getSubmitModalRowKey(node.data);
      if (key) selectedKeys.add(key);
    });

    this.gridApi.setRowData(nextRowData);
    this.lastSyncedRowSignature = signature;
    this.gridApi.refreshCells({ force: true });

    if (selectedKeys.size > 0) {
      this.batchSelecting = true;
      this.gridApi.forEachNode((node) => {
        const key = getSubmitModalRowKey(node.data);
        if (key && selectedKeys.has(key)) {
          node.setSelected(true);
        }
      });
      this.batchSelecting = false;
    }

    this.refreshExternalFilter();

    if (this.isInitialLoad || this.pendingAutoSelect) {
      this.scheduleAutoSelectIfNeeded();
    }
  }

  private readonly resizeHandler = (): void => this.resizeGrid();

  handleGridReady(e: CustomEvent): void {
    this.gridApi = e.detail.event.api;
    if (!this.gridOptions && this.series) {
      this.setGridOptions(this.series.TAX_STATUS as TaxStatusEnum);
    }
    if (this.gridOptions) {
      this.gridApi.setColumnDefs(this.gridOptions.columnDefs);
    }
    if (this.rowData != null) {
      this.applyOrdersFilter();
      this.syncGridRowData();
    }
    this.refreshExternalFilter();
    if (this.isInitialLoad) {
      this.syncSelectionForOpen();
    }
    window.addEventListener('resize', this.resizeHandler);
  }

  /** Current AG Grid selection for this series (source of truth for submit enablement). */
  getSelectedOrderRows(): MaturityOrder[] {
    if (!this.gridApi) return [];
    const rows: MaturityOrder[] = [];
    this.gridApi.forEachNode((node) => {
      if (node.isSelected() && node.data) {
        rows.push(node.data);
      }
    });
    return rows;
  }

  private emitSelected(): void {
    const rows = this.getSelectedOrderRows();
    this.$emit('selected-orders', rows);
  }

  /** Auto-select selectable rows once data is rendered; defer until AG Grid has displayed rows. */
  private scheduleAutoSelectIfNeeded(): void {
    if ((!this.isInitialLoad && !this.pendingAutoSelect) || !this.gridApi || this.cancellation) {
      return;
    }

    this.cancelScheduledAutoSelect();

    const runAutoSelect = (): void => {
      this.autoSelectFrameRef = null;
      if (!this.gridApi || this.cancellation) return;
      if (this.gridApi.getDisplayedRowCount() === 0) return;

      if (this.hasSelectableDisplayedRows()) {
        this.autoSelectValidRows();
      }

      this.isInitialLoad = false;
      this.pendingAutoSelect = false;
    };

    if (typeof requestAnimationFrame === 'function') {
      this.autoSelectFrameRef = requestAnimationFrame(runAutoSelect);
    } else {
      runAutoSelect();
    }
  }

  private cancelScheduledAutoSelect(): void {
    if (this.autoSelectFrameRef == null) return;
    if (typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.autoSelectFrameRef);
    }
    this.autoSelectFrameRef = null;
  }

  private hasSelectableDisplayedRows(): boolean {
    if (!this.gridApi) return false;
    let found = false;
    this.gridApi.forEachNodeAfterFilter((node) => {
      if (isOrderSelectableInSubmitModal(node.data)) found = true;
    });
    return found;
  }

  private autoSelectValidRows(): void {
    if (!this.gridApi || this.cancellation) return;
    this.batchSelecting = true;
    try {
      this.gridApi.deselectAll();
      this.gridApi.forEachNodeAfterFilter((node: IRowNode<MaturityOrder>) => {
        if (isOrderSelectableInSubmitModal(node.data)) {
          node.setSelected(true);
        }
      });
    } finally {
      this.batchSelecting = false;
    }
    this.emitSelected();
  }

  private resizeGrid(): void {
    clearTimeout(this.gridResizeTimeoutRef);
    this.gridResizeTimeoutRef = setTimeout(() => {
      this.gridApi?.sizeColumnsToFit();
    }, 100);
  }

  disconnectedCallback(): void {
    window.removeEventListener('resize', this.resizeHandler);
    clearTimeout(this.gridResizeTimeoutRef);
    this.cancelScheduledAutoSelect();
    this.gridApi = null;
    this.gridOptions = undefined;
    this.lastSyncedRowSignature = null;
    this.isInitialLoad = true;
    this.pendingAutoSelect = false;
    this.displayedRowCount = 0;
    super.disconnectedCallback();
  }
}
